/**
 * Chutt... - RepScore Gauge
 * Jauge visuelle pour le score de reputation
 * Design system: teal (bon), warning (moyen), danger (critique)
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles/colors';

// Seuils et couleurs - Design system Chutt...
const SCORE_LEVELS = [
  { min: 90, max: 100, label: 'Excellent', color: colors.success, description: 'Reputation solide' },
  { min: 75, max: 89, label: 'Bon', color: colors.accent, description: 'Bonne dynamique' },
  { min: 50, max: 74, label: 'Moyen', color: colors.warning, description: 'A surveiller' },
  { min: 25, max: 49, label: 'Fragile', color: colors.warningDark, description: 'Vigilance requise' },
  { min: 0, max: 24, label: 'Critique', color: colors.danger, description: 'Intervention necessaire' },
];

const getScoreLevel = (score) => {
  return SCORE_LEVELS.find(level => score >= level.min && score <= level.max) || SCORE_LEVELS[4];
};

function RepScoreGauge({
  score = 0,
  previousScore = null,
  showTrend = true,
  size = 'large', // 'small' | 'medium' | 'large'
  showDetails = true,
}) {
  // Handle null/undefined score (no data available)
  const hasData = score !== null && score !== undefined;
  const displayScore = hasData ? Math.max(0, Math.min(100, score)) : 0;
  const level = getScoreLevel(displayScore);

  // Calculate trend: if previousScore is a valid number, use it directly
  const hasPrevious = previousScore !== null && previousScore !== undefined && hasData;
  const trend = hasPrevious ? Math.round(score - previousScore) : 0;

  // Sizes: medium made larger for better dashboard presence
  const sizes = {
    small: { gauge: 120, strokeWidth: 10, fontSize: 28, subSize: '0.75rem', labelSize: '0.75rem', dotRadius: 5 },
    medium: { gauge: 160, strokeWidth: 14, fontSize: 38, subSize: '0.85rem', labelSize: '0.85rem', dotRadius: 6 },
    large: { gauge: 220, strokeWidth: 16, fontSize: 52, subSize: '1rem', labelSize: '1rem', dotRadius: 7 },
  };
  const s = sizes[size] || sizes.large;

  // Arc calculations
  const padding = 16;
  const radius = (s.gauge - padding * 2) / 2;
  const circumference = Math.PI * radius; // Half circle
  const progress = hasData ? (displayScore / 100) * circumference : 0;
  const centerY = s.gauge / 2 + padding / 2;

  // Unique ID for SVG filters to avoid conflicts with multiple gauges
  const filterId = `scoreGlow-${size}-${Math.random().toString(36).substr(2, 5)}`;
  const gradientId = `scoreGrad-${size}-${Math.random().toString(36).substr(2, 5)}`;

  return (
    <Box
      sx={{
        p: size === 'small' ? 1 : 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      {/* Jauge SVG */}
      <Box sx={{
        position: 'relative',
        width: s.gauge,
        height: s.gauge / 2 + 24,
      }}>
        <svg
          width={s.gauge}
          height={s.gauge / 2 + 24}
          viewBox={`0 0 ${s.gauge} ${s.gauge / 2 + 24}`}
        >
          <defs>
            <filter id={filterId}>
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={level.color} stopOpacity={0.7} />
              <stop offset="100%" stopColor={level.color} stopOpacity={1} />
            </linearGradient>
          </defs>

          {/* Background arc - subtle track */}
          <path
            d={`M ${padding} ${centerY} A ${radius} ${radius} 0 0 1 ${s.gauge - padding} ${centerY}`}
            fill="none"
            stroke={colors.gray200}
            strokeWidth={s.strokeWidth}
            strokeLinecap="round"
          />

          {/* Progress arc with gradient */}
          {hasData && (
            <path
              d={`M ${padding} ${centerY} A ${radius} ${radius} 0 0 1 ${s.gauge - padding} ${centerY}`}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={s.strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${progress} ${circumference}`}
              filter={`url(#${filterId})`}
              style={{
                transition: 'stroke-dasharray 1s ease-out, stroke 0.4s ease',
              }}
            />
          )}

          {/* Indicator dot at end of progress */}
          {hasData && displayScore > 0 && (() => {
            const angle = Math.PI - (displayScore / 100) * Math.PI;
            const indicatorX = s.gauge / 2 + radius * Math.cos(angle);
            const indicatorY = centerY - radius * Math.sin(angle);
            return (
              <>
                <circle
                  cx={indicatorX}
                  cy={indicatorY}
                  r={s.dotRadius + 3}
                  fill={level.color}
                  opacity={0.2}
                  style={{ transition: 'cx 1s ease-out, cy 1s ease-out' }}
                />
                <circle
                  cx={indicatorX}
                  cy={indicatorY}
                  r={s.dotRadius}
                  fill={level.color}
                  stroke="white"
                  strokeWidth="2.5"
                  style={{
                    transition: 'cx 1s ease-out, cy 1s ease-out',
                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.15))',
                  }}
                />
              </>
            );
          })()}

          {/* Scale markers at 0, 25, 50, 75, 100 */}
          {size !== 'small' && [0, 25, 50, 75, 100].map((mark) => {
            const angle = Math.PI - (mark / 100) * Math.PI;
            const outerR = radius + s.strokeWidth / 2 + 2;
            const innerR = radius + s.strokeWidth / 2 + 6;
            const x1 = s.gauge / 2 + outerR * Math.cos(angle);
            const y1 = centerY - outerR * Math.sin(angle);
            const x2 = s.gauge / 2 + innerR * Math.cos(angle);
            const y2 = centerY - innerR * Math.sin(angle);
            return (
              <line
                key={mark}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={colors.gray300}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {/* Score number in center */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.3 }}>
            <Typography
              sx={{
                fontSize: s.fontSize,
                fontWeight: 800,
                color: hasData ? level.color : colors.gray400,
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              {hasData ? Math.round(displayScore) : '--'}
            </Typography>
            {hasData && (
              <Typography
                sx={{
                  fontSize: s.subSize,
                  fontWeight: 500,
                  color: colors.gray400,
                  lineHeight: 1,
                }}
              >
                /100
              </Typography>
            )}
          </Box>
          {!hasData && (
            <Typography
              sx={{
                fontSize: s.subSize,
                color: colors.gray400,
                mt: 0.3,
              }}
            >
              Pas de donnees
            </Typography>
          )}
        </Box>
      </Box>

      {/* Label and description */}
      {showDetails && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          {hasData ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                bgcolor: `${level.color}10`,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: level.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  color: level.color,
                  fontSize: size === 'small' ? '0.75rem' : '0.85rem',
                }}
              >
                {level.label}
              </Typography>
              <Typography
                sx={{
                  color: colors.gray500,
                  fontSize: size === 'small' ? '0.65rem' : '0.75rem',
                  ml: 0.5,
                }}
              >
                {level.description}
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontWeight: 500,
                color: colors.gray400,
                fontSize: size === 'small' ? '0.75rem' : '0.85rem',
              }}
            >
              En attente de donnees
            </Typography>
          )}
        </Box>
      )}

      {/* Trend indicator — no arrows, 3-color: >=10 pts green, <=-10 pts red, between orange */}
      {showTrend && hasData && hasPrevious && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            px: 1.5,
            py: 0.4,
            borderRadius: 1.5,
            bgcolor: trend >= 10 ? colors.successBg : trend <= -10 ? colors.dangerBg : colors.warningBg,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: trend >= 10 ? colors.success : trend <= -10 ? colors.danger : colors.warning,
            }}
          >
            {trend === 0 ? 'Stable' : `${trend > 0 ? '+' : ''}${trend} pt${Math.abs(trend) > 1 ? 's' : ''}`}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.65rem',
              color: colors.gray500,
              ml: 0.3,
            }}
          >
            vs periode prec.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default RepScoreGauge;
